import { validationFns } from "../../../../components/form/validations";
import { EnvVarInput, VolumeInput, WorkloadInput } from "../../../../schema/types/workload/input";
import { formSet } from "../../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { NameValuePair } from "../../../../schema/types/base";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import jsYaml from "js-yaml";
import { validateContainerResources, getFieldPathForValidation } from "./resourceValidation";
import { getUsedSecretLinks } from "./utils";

// TODO can convert this to Zod

export function getValidator(id: string) {
  return async function validator(v: WorkloadInput): Promise<boolean> {
    // setup

    const validationCallId = uuidv4();

    formSet(id, `meta.valid`, false);
    formSet(id, `meta.validating`, true);
    formSet(id, `meta.validationCallId`, validationCallId);
    formSet(id, `errors`, {});
    formSet(id, `warnings`, {});

    // Paths<WorkloadInput>
    const e: Partial<Record<string, { message: string }>> = {};
    const w: Partial<Record<string, { message: string }>> = {};

    function setError(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      e[path] = { message: message };
    }

    function setWarning(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      const currentWarning = w[path];
      if (currentWarning) {
        return;
      }
      w[path] = { message: message };
    }

    // setup end

    try {
      const fs = validationFns("Name");
      for (const f of [fs.required, fs.workload.name]) {
        const r = f(v.name);
        if (r !== true) {
          setError(`name`, r);
        }
      }

      if (v.description.startsWith(" ")) {
        setWarning(`description`, "Description starts with empty character");
      }
      if (v.description.endsWith(" ")) {
        setWarning(`description`, "Description ends with empty character");
      }

      if (!v.gvc?.link) {
        setError(`gvc`, "GVC is required");
      } else {
        const parsedLink = parseLink(v.gvc.link, { useInputCtx: true });
        if (!parsedLink.isValid) {
          setError(`gvc`, parsedLink.errorMessage);
        }
      }

      // Identity validation
      const usedSecretLinks = getUsedSecretLinks(v.containers);
      const identityIsRequired = usedSecretLinks.length > 0;

      if (identityIsRequired) {
        if (!v.identity.value.link) {
          setError(`identity.value`, "Identity is required when using secrets");
        } else {
          // Validate identity belongs to same GVC
          const parsedIdentity = parseLink(v.identity.value.link, { useInputCtx: true });
          const workloadGvc = parseLink(v.gvc.link, { useInputCtx: true }).gvc;

          if (parsedIdentity.gvc && workloadGvc && parsedIdentity.gvc !== workloadGvc) {
            setError(`identity.value`, `Identity must belong to the same GVC as the workload`);
          } else if (!v.identity.isValidating && !v.identity.isValid) {
            // Only set error when validation is complete and identity is invalid
            // Skip validation check while in progress - the UI shows a spinner
            setError(`identity.value`, v.identity.invalidError || "Identity is invalid");
          }
        }
      }

      let x: string;
      let xn: number;

      // job spec start
      if (v.type === "cron") {
        const CRON_REGEX =
          /^(\*|(\d+(-\d+)?))(\/\d+)?(,(\*|(\d+(-\d+)?))(\/\d+)?)*(\s(\*|(\d+(-\d+)?))(\/\d+)?(,(\*|(\d+(-\d+)?))(\/\d+)?)*){4}$/;
        const validateScheduleField = (schedule: string, fieldPath: string) => {
          if (!schedule) {
            setError(fieldPath, "Schedule is required");
          } else if (!CRON_REGEX.test(schedule) || schedule.split(/\s+/).length !== 5) {
            setError(fieldPath, "Schedule does not match the standard cron format");
          }
        };

        validateScheduleField(v.job.schedule, `job.schedule`);

        if (v.job.historyLimit) {
          xn = Number(v.job.historyLimit);
          if (Number.isNaN(xn)) {
            setError(`job.historyLimit`, "History Limit must be a number");
          } else {
            if (xn < 1) {
              setError(`job.historyLimit`, "History Limit must be at least 1");
            }
            if (xn > 20) {
              setError(`job.historyLimit`, "History Limit can be at most 20");
            }
          }
        }
        if (v.job.activeDeadlineSeconds) {
          xn = Number(v.job.activeDeadlineSeconds);
          if (Number.isNaN(xn)) {
            setError(`job.activeDeadlineSeconds`, "Active Deadline must be a number");
          } else {
            if (xn < 1) {
              setError(`job.activeDeadlineSeconds`, "Active Deadline must be at least 1");
            }
            if (xn > 86400) {
              setError(`job.activeDeadlineSeconds`, "Active Deadline can be at most 86400");
            }
          }
        }
      }
      // job spec end

      // default options
      if (v.defaultOptions.alreadySet || v.defaultOptions.override) {
        if (v.defaultOptions.capacityAIUpdateMinutes) {
          x = v.defaultOptions.capacityAIUpdateMinutes;
          xn = Number(x);
          if (Number.isNaN(xn)) {
            setError("defaultOptions.capacityAIUpdateMinutes", "Capacity AI Update Minutes must be a number");
          } else {
            if (xn < 2) {
              setError("defaultOptions.capacityAIUpdateMinutes", "Capacity AI Update Minutes must be at least 2");
            }
          }
        }

        if (v.type !== "cron") {
          if (v.defaultOptions.timeoutSeconds) {
            x = v.defaultOptions.timeoutSeconds;
            xn = Number(x);
            if (Number.isNaN(xn)) {
              setError("defaultOptions.timeoutSeconds", "Timeout Seconds must be a number");
            } else {
              if (xn < 1) {
                setError("defaultOptions.timeoutSeconds", "Timeout Seconds must be at least 1");
              }
              if (xn > 60 * 60) {
                setError("defaultOptions.timeoutSeconds", "Timeout Seconds can be at most 3600 (1 hour)");
              }
            }
          }

          // Add warning for low timeout when minScale is 0 (serverless only)
          if (v.type === "serverless") {
            const minScale = Number(v.defaultOptions.autoscaling?.minScale || "1");
            const timeout = Number(v.defaultOptions.timeoutSeconds || "0");
            if (minScale === 0 && timeout > 0 && timeout < 60) {
              setWarning(
                "defaultOptions.timeoutSeconds",
                "Scaling up from zero might require 60 seconds or more to spin up",
              );
            }
          }

          if (v.defaultOptions.autoscaling) {
            if (v.defaultOptions.autoscaling.metric === "multi") {
              if (
                !v.defaultOptions.autoscaling.multi.cpu &&
                !v.defaultOptions.autoscaling.multi.memory &&
                !v.defaultOptions.autoscaling.multi.rps
              ) {
                setError(
                  "defaultOptions.autoscaling.multi",
                  "At least 1 metric must be set for multi metric autoscaling",
                );
              }
            }

            const minScale = Number(v.defaultOptions.autoscaling.minScale || "1"); // min 0
            const maxScale = Number(v.defaultOptions.autoscaling.maxScale || "5"); // min 0

            if (minScale < 0) {
              setError("defaultOptions.autoscaling.minScale", "Min Scale must be at least 0");
            }

            if (v.defaultOptions.autoscaling.metric !== "disabled") {
              if (minScale > maxScale) {
                setError("defaultOptions.autoscaling.minScale", "Min Scale cannot be more than Max Scale");
              }
              if (maxScale < 0) {
                setError("defaultOptions.autoscaling.maxScale", "Max Scale must be at least 0");
              }
              if (maxScale < minScale) {
                setError("defaultOptions.autoscaling.maxScale", "Max Scale cannot be less than Min Scale");
              }
            }

            if (v.defaultOptions.autoscaling.scaleToZeroDelay) {
              x = v.defaultOptions.autoscaling.scaleToZeroDelay;
              xn = Number(x);
              if (Number.isNaN(xn)) {
                setError("defaultOptions.autoscaling.scaleToZeroDelay", "Scale to Zero must be a number");
              } else {
                if (xn < 30) {
                  setError("defaultOptions.autoscaling.scaleToZeroDelay", "Scale to Zero must be at least 30");
                }
                if (xn > 3600) {
                  setError("defaultOptions.autoscaling.scaleToZeroDelay", "Scale to Zero can be at most 3600 (1 hour)");
                }
              }
            }

            if (v.type === "serverless") {
              if (v.defaultOptions.autoscaling.maxConcurrency) {
                x = v.defaultOptions.autoscaling.maxConcurrency;
                xn = Number(x);
                if (Number.isNaN(xn)) {
                  setError("defaultOptions.autoscaling.maxConcurrency", "Max Concurrency must be a number");
                } else {
                  if (xn < 0) {
                    setError("defaultOptions.autoscaling.maxConcurrency", "Max Concurrency must be at least 0");
                  }
                  if (xn > 30000) {
                    setError("defaultOptions.autoscaling.maxConcurrency", "Max Concurrency can be at most 30000");
                  }
                }
              }
            }

            // KEDA YAML validation - check kedaRaw which is set when YAML parsing fails
            if (v.defaultOptions.autoscaling.metric === "keda" && v.defaultOptions.meta?.kedaRaw) {
              setError("defaultOptions.autoscaling.keda", "KEDA configuration must be valid YAML");
            }
          }
        }
      }
      // default options end

      // local options
      for (const localOptionsIndexStr in v.localOptions) {
        const localOptionsIndex = Number(localOptionsIndexStr);
        const localOptions = v.localOptions[localOptionsIndex];

        if (!localOptions.location) {
          setError(`localOptions.${localOptionsIndex}.location`, "Location is required");
        }

        if (v.type !== "cron") {
          if (localOptions.timeoutSeconds) {
            x = localOptions.timeoutSeconds;
            xn = Number(x);
            if (Number.isNaN(xn)) {
              setError(`localOptions.${localOptionsIndex}.timeoutSeconds`, "Timeout Seconds must be a number");
            } else {
              if (xn < 1) {
                setError(`localOptions.${localOptionsIndex}.timeoutSeconds`, "Timeout Seconds must be at least 1");
              }
              if (xn > 60 * 60) {
                setError(
                  `localOptions.${localOptionsIndex}.timeoutSeconds`,
                  "Timeout Seconds can be at most 3600 (1 hour)",
                );
              }
            }
          }

          // Add warning for low timeout when minScale is 0 (serverless only, minScale is on defaultOptions)
          if (v.type === "serverless") {
            const minScale = Number(v.defaultOptions.autoscaling?.minScale || "1");
            const timeout = Number(localOptions.timeoutSeconds || "0");
            if (minScale === 0 && timeout > 0 && timeout < 60) {
              setWarning(
                `localOptions.${localOptionsIndex}.timeoutSeconds`,
                "Scaling up from zero might require 60 seconds or more to spin up",
              );
            }
          }

          if (localOptions.capacityAIUpdateMinutes) {
            x = localOptions.capacityAIUpdateMinutes;
            xn = Number(x);
            if (Number.isNaN(xn)) {
              setError(
                `localOptions.${localOptionsIndex}.capacityAIUpdateMinutes`,
                "Capacity AI Update Minutes must be a number",
              );
            } else {
              if (xn < 2) {
                setError(
                  `localOptions.${localOptionsIndex}.capacityAIUpdateMinutes`,
                  "Capacity AI Update Minutes must be at least 2",
                );
              }
            }
          }

          if (localOptions.autoscaling) {
            if (localOptions.autoscaling.metric === "multi") {
              if (
                !localOptions.autoscaling.multi.cpu &&
                !localOptions.autoscaling.multi.memory &&
                !localOptions.autoscaling.multi.rps
              ) {
                setError(
                  `localOptions.${localOptionsIndex}.autoscaling.multi`,
                  "At least 1 metric must be set for multi metric autoscaling",
                );
              }
            }

            const minScale = Number(localOptions.autoscaling.minScale || "1"); // min 0
            const maxScale = Number(localOptions.autoscaling.maxScale || "5"); // min 0

            if (minScale < 0) {
              setError(`localOptions.${localOptionsIndex}.autoscaling.minScale`, "Min Scale must be at least 0");
            }

            if (localOptions.autoscaling.metric !== "disabled") {
              if (minScale > maxScale) {
                setError(
                  `localOptions.${localOptionsIndex}.autoscaling.minScale`,
                  "Min Scale cannot be more than Max Scale",
                );
              }
              if (maxScale < 0) {
                setError(`localOptions.${localOptionsIndex}.autoscaling.maxScale`, "Max Scale must be at least 0");
              }
              if (maxScale < minScale) {
                setError(
                  `localOptions.${localOptionsIndex}.autoscaling.maxScale`,
                  "Max Scale cannot be less than Min Scale",
                );
              }
            }

            if (localOptions.autoscaling.scaleToZeroDelay) {
              x = localOptions.autoscaling.scaleToZeroDelay;
              xn = Number(x);
              if (Number.isNaN(xn)) {
                setError(
                  `localOptions.${localOptionsIndex}.autoscaling.scaleToZeroDelay`,
                  "Scale to Zero must be a number",
                );
              } else {
                if (xn < 30) {
                  setError(
                    `localOptions.${localOptionsIndex}.autoscaling.scaleToZeroDelay`,
                    "Scale to Zero must be at least 30",
                  );
                }
                if (xn > 3600) {
                  setError(
                    `localOptions.${localOptionsIndex}.autoscaling.scaleToZeroDelay`,
                    "Scale to Zero can be at most 3600 (1 hour)",
                  );
                }
              }
            }

            if (v.type === "serverless") {
              if (localOptions.autoscaling.maxConcurrency) {
                x = localOptions.autoscaling.maxConcurrency;
                xn = Number(x);
                if (Number.isNaN(xn)) {
                  setError(
                    `localOptions.${localOptionsIndex}.autoscaling.maxConcurrency`,
                    "Max Concurrency must be a number",
                  );
                } else {
                  if (xn < 0) {
                    setError(
                      `localOptions.${localOptionsIndex}.autoscaling.maxConcurrency`,
                      "Max Concurrency must be at least 0",
                    );
                  }
                  if (xn > 30000) {
                    setError(
                      `localOptions.${localOptionsIndex}.autoscaling.maxConcurrency`,
                      "Max Concurrency can be at most 30000",
                    );
                  }
                }
              }
            }

            // KEDA YAML validation - check kedaRaw which is set when YAML parsing fails
            if (localOptions.autoscaling.metric === "keda" && localOptions.meta?.kedaRaw) {
              setError(
                `localOptions.${localOptionsIndex}.autoscaling.keda`,
                "KEDA configuration must be valid YAML",
              );
            }
          }
        }
      }
      // local options end

      // containers start
      for (const containerIndexStr in v.containers) {
        const containerIndex = Number(containerIndexStr);
        const container = v.containers[containerIndex];

        if (container.image.isExternal && !container.image.externalValue) {
          setError(`containers.${containerIndex}.imageroot`, "Image is required");
        }
        if (!container.image.isExternal && !container.image.cplnValue.value) {
          setError(`containers.${containerIndex}.image.cplnValue`, "Image is required");
          setError(`containers.${containerIndex}.imageroot`, "Image is required");
        }
        // Only warn for user-typed images that we tried to fetch but couldn't find
        if (
          !container.image.isExternal &&
          !!container.image.cplnValue.link &&
          !container.image.isFromSavedWorkload &&
          container.image.cplnFetchAttempted &&
          !container.image.cplnValue.item
        ) {
          setWarning(
            `containers.${containerIndex}.imageroot`,
            "This image was not found in CPLN Repo or you don't have access to it, make sure the correct repo option is selected. If you are sure that the image exists, workload can still use it even if you cannot access the image item.",
          );
        }

        if (!container.name.value) {
          setError(`containers.${containerIndex}.name.value`, "Name is required");
        }
        if (container.name.value.startsWith(`cpln-`)) {
          setError(`containers.${containerIndex}.name.value`, "Name cannot start with cpln-");
        }
        if (container.name.value.startsWith(`debugger-`)) {
          setError(`containers.${containerIndex}.name.value`, "Name cannot start with debugger-");
        }

        if (container.name.value.startsWith(" ")) {
          setWarning(`containers.${containerIndex}.name.value`, "Name starts with empty character");
        }
        if (container.name.value.endsWith(" ")) {
          setWarning(`containers.${containerIndex}.name.value`, "Name ends with empty character");
        }

        // at least 1 serves traffic
        if (v.type === "serverless") {
          const hasServesTraffic = v.containers.some((c) => c.servesTraffic);
          if (!hasServesTraffic) {
            setError(`containersservestrafficroot`, "At least 1 container must have Serves Traffic enabled");
          }

          const servesTraffic = container.servesTraffic;
          if (servesTraffic) {
            if (!container.ports[0]?.protocol) {
              setError(`containers.${containerIndex}.ports.${0}.protocol`, "Protocol is required");
              setError(`containers.${containerIndex}.portsroot`, "Protocol is required");
            }
            if (!container.ports[0]?.number) {
              setError(`containers.${containerIndex}.ports.${0}.number`, "Number is required");
              setError(`containers.${containerIndex}.portsroot`, "Number is required");
            }
          }
        } else {
          for (const portIndexStr in container.ports) {
            const portIndex = Number(portIndexStr);
            const port = container.ports[portIndex];

            if (!port.number) {
              setError(`containers.${containerIndex}.ports.${portIndex}.number`, "Number is required");
              setError(`containers.${containerIndex}.portsroot`, "Port numbers are required");
            }
            if (!port.protocol) {
              setError(`containers.${containerIndex}.ports.${portIndex}.protocol`, "Protocol is required");
              setError(`containers.${containerIndex}.portsroot`, "Port numbers are required");
            }

            // Unique port
            for (const _containerIndexStr in v.containers) {
              const _containerIndex = Number(_containerIndexStr);
              const _container = v.containers[_containerIndex];

              for (const _portIndexStr in _container.ports) {
                const _portIndex = Number(_portIndexStr);
                const _port = _container.ports[_portIndex];

                if (_portIndex === portIndex && _containerIndex === containerIndex) {
                  continue;
                }

                if (port.number && _port.number) {
                  if (port.number === _port.number) {
                    setError(
                      `containers.${containerIndex}.ports.${portIndex}.number`,
                      `Number must be unique across containers _c ${_containerIndex} _p ${_portIndex}`,
                    );
                    setError(`containers.${containerIndex}.portsroot`, "Port numbers must be unique across containers");
                  }
                }
              }
            }
          }
        }

        // env start
        for (const envIndexStr in container.env) {
          const envIndex = Number(envIndexStr);
          const env = container.env[envIndex] as EnvVarInput;

          if (!env.name) {
            setError(`containers.${containerIndex}.env.${envIndex}.name`, "Name is required");
            setError(`containers.${containerIndex}.envroot`, "Environment variable names are required");
          }
          if (!env.prefix) {
            if (env.text.startsWith(" ") || env.text.endsWith(" ")) {
              setWarning(`containers.${containerIndex}.env.${envIndex}.text`, "Value starts or ends with empty character");
              setWarning(`containers.${containerIndex}.envroot`, "Some values start or end with empty character");
            }
          }
          if (env.prefix === "cpln://secret/") {
            if (!env.cplnSecret.value) {
              setError(`containers.${containerIndex}.env.${envIndex}.cplnSecret`, "Secret value is required");
              setError(`containers.${containerIndex}.envroot`, "Secret values are required");
            }
            if (env.cplnSecret.value.includes(" ")) {
              setWarning(`containers.${containerIndex}.env.${envIndex}.cplnSecret`, "Value contains empty character");
              setWarning(`containers.${containerIndex}.envroot`, "Some values contain empty character");
            }
            if (env.suffix.includes(" ")) {
              setWarning(`containers.${containerIndex}.env.${envIndex}.suffix`, "Suffix contains empty character");
              setWarning(`containers.${containerIndex}.envroot`, "Some suffix values contain empty character");
            }
          }
          if (env.prefix === "k8s://secret/") {
            if (!env.k8sSecret) {
              setError(`containers.${containerIndex}.env.${envIndex}.k8sSecret`, "K8s Secret value is required");
              setError(`containers.${containerIndex}.envroot`, "K8s Secret values are required");
            }
            if (env.k8sSecret.includes(" ")) {
              setWarning(`containers.${containerIndex}.env.${envIndex}.k8sSecret`, "Value contains empty character");
              setWarning(`containers.${containerIndex}.envroot`, "Some values contain empty character");
            }
          }
          if (env.prefix === "cpln://reference/") {
            if (!env.reference) {
              setError(`containers.${containerIndex}.env.${envIndex}.reference`, "Reference value is required");
              setError(`containers.${containerIndex}.envroot`, "Reference values are required");
            }
            if (env.reference.includes(" ")) {
              setWarning(`containers.${containerIndex}.env.${envIndex}.reference`, "Value contains empty character");
              setWarning(`containers.${containerIndex}.envroot`, "Some values contain empty character");
            }
          }

          for (const _envIndexStr in container.env) {
            const _envIndex = Number(_envIndexStr);
            if (_envIndex === envIndex) {
              continue;
            }

            const _env = container.env[_envIndex] as EnvVarInput;
            if (_env.name === env.name) {
              setWarning(`containers.${containerIndex}.env.${envIndex}.name`, "Name is not unique");
              setWarning(`containers.${containerIndex}.envroot`, "Some names are not unique");
            }
          }

          if (container.inheritEnv) {
            for (const gvcEnv of v.gvc.item?.spec.env || []) {
              if (gvcEnv.name === env.name) {
                setWarning(
                  `containers.${containerIndex}.env.${envIndex}.name`,
                  "Overrides inherited GVC environment variable",
                );
                setWarning(`containers.${containerIndex}.envroot`, "Some variable override GVC inherited variables");
              }
            }
          }
        }
        // env end

        // volume start
        for (const volumeIndexStr in container.volumes) {
          const volumeIndex = Number(volumeIndexStr);
          const volume = container.volumes[volumeIndex] as VolumeInput;

          if (!volume.path) {
            setError(`containers.${containerIndex}.volumes.${volumeIndex}.path`, "Path is required");
            setError(`containers.${containerIndex}.volumesroot`, "Volume paths are required");
          }

          // Validate based on uriType (following EnvVarInput validation pattern)
          switch (volume.uriType) {
            case "cpln://secret/": {
              if (!volume.cplnSecret.value) {
                setError(`containers.${containerIndex}.volumes.${volumeIndex}.cplnSecret`, "Secret is required");
                setError(`containers.${containerIndex}.volumesroot`, "Secret values are required");
              }
              if (volume.cplnSecret.value?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.cplnSecret`,
                  "Secret contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some values contain empty character");
              }
              if (volume.cplnSecretProperty?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.cplnSecretProperty`,
                  "Property contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some property values contain empty character");
              }
              break;
            }
            case "k8s://secret/": {
              if (!volume.k8sSecret) {
                setError(`containers.${containerIndex}.volumes.${volumeIndex}.k8sSecret`, "K8s Secret is required");
                setError(`containers.${containerIndex}.volumesroot`, "K8s Secret values are required");
              }
              if (volume.k8sSecret?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.k8sSecret`,
                  "K8s Secret contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some values contain empty character");
              }
              break;
            }
            case "cpln://volumeset/": {
              if (!volume.volumeSet.value) {
                setError(`containers.${containerIndex}.volumes.${volumeIndex}.volumeSet`, "Volume Set is required");
                setError(`containers.${containerIndex}.volumesroot`, "Volume Set values are required");
              }
              if (volume.volumeSet.value?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.volumeSet`,
                  "Volume Set contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some values contain empty character");
              }
              break;
            }
            case "s3://": {
              if (!volume.s3) {
                setError(`containers.${containerIndex}.volumes.${volumeIndex}.s3`, "S3 bucket path is required");
                setError(`containers.${containerIndex}.volumesroot`, "S3 bucket paths are required");
              }
              if (volume.s3?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.s3`,
                  "S3 path contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some values contain empty character");
              }
              break;
            }
            case "gs://": {
              if (!volume.gs) {
                setError(`containers.${containerIndex}.volumes.${volumeIndex}.gs`, "Google bucket path is required");
                setError(`containers.${containerIndex}.volumesroot`, "Google bucket paths are required");
              }
              if (volume.gs?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.gs`,
                  "Google bucket path contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some values contain empty character");
              }
              break;
            }
            case "azureblob://": {
              if (!volume.azureBlob) {
                setError(
                  `containers.${containerIndex}.volumes.${volumeIndex}.azureBlob`,
                  "Azure Blob path is required",
                );
                setError(`containers.${containerIndex}.volumesroot`, "Azure Blob paths are required");
              }
              if (volume.azureBlob?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.azureBlob`,
                  "Azure Blob path contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some values contain empty character");
              }
              break;
            }
            case "azurefs://": {
              if (!volume.azureFs) {
                setError(
                  `containers.${containerIndex}.volumes.${volumeIndex}.azureFs`,
                  "Azure File Share path is required",
                );
                setError(`containers.${containerIndex}.volumesroot`, "Azure File Share paths are required");
              }
              if (volume.azureFs?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.azureFs`,
                  "Azure File Share path contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some values contain empty character");
              }
              break;
            }
            case "scratch://": {
              if (!volume.scratch) {
                setError(`containers.${containerIndex}.volumes.${volumeIndex}.scratch`, "Scratch name is required");
                setError(`containers.${containerIndex}.volumesroot`, "Scratch names are required");
              }
              if (volume.scratch?.includes(" ")) {
                setWarning(
                  `containers.${containerIndex}.volumes.${volumeIndex}.scratch`,
                  "Scratch name contains empty character",
                );
                setWarning(`containers.${containerIndex}.volumesroot`, "Some values contain empty character");
              }
              break;
            }
          }

          // Check for unique paths
          for (const _volumeIndexStr in container.volumes) {
            const _volumeIndex = Number(_volumeIndexStr);
            if (_volumeIndex === volumeIndex) {
              continue;
            }

            const _volume = container.volumes[_volumeIndex] as VolumeInput;
            if (_volume.path && _volume.path === volume.path) {
              setWarning(`containers.${containerIndex}.volumes.${volumeIndex}.path`, "Path is not unique");
              setWarning(`containers.${containerIndex}.volumesroot`, "Some paths are not unique");
            }
          }
        }
        // volume end

        // readiness probe start
        if (container.readinessProbe.alreadySet || container.readinessProbe.override) {
          if (container.readinessProbe.type === "exec") {
            if (!container.readinessProbe.exec.command) {
              setError(`containers.${containerIndex}.readinessProbe.exec.command`, "Command is required");
            }

            for (const argIndexStr in container.readinessProbe.exec.args) {
              const argIndex = Number(argIndexStr);
              const arg = container.readinessProbe.exec.args[argIndex];
              if (!arg.value) {
                setError(`containers.${containerIndex}.readinessProbe.exec.args.${argIndex}.value`, "Arg is required");
                setError(
                  `containers.${containerIndex}.readinessProbe.exec.argsroot`,
                  "Some arg fields are empty, either type an arg or remove it",
                );
              }
            }
          }

          if (container.readinessProbe.type === "http") {
            for (const headerIndexStr in container.readinessProbe.httpGet.httpHeaders) {
              const headerIndex = Number(headerIndexStr);
              const header = container.readinessProbe.httpGet.httpHeaders[headerIndex];
              if (!header.name) {
                setError(
                  `containers.${containerIndex}.readinessProbe.httpGet.httpHeaders.${headerIndex}.name`,
                  "Name is required",
                );
                setError(
                  `containers.${containerIndex}.readinessProbe.httpGet.httpHeadersroot`,
                  "Header names are required",
                );
              }
              if (!header.value) {
                setError(
                  `containers.${containerIndex}.readinessProbe.httpGet.httpHeaders.${headerIndex}.value`,
                  "Value is required",
                );
                setError(
                  `containers.${containerIndex}.readinessProbe.httpGet.httpHeadersroot`,
                  "Header values are required",
                );
              }
              if (header.value.length > 128) {
                setError(
                  `containers.${containerIndex}.readinessProbe.httpGet.httpHeaders.${headerIndex}.value`,
                  "Value can be at most 128 characters",
                );
                setError(
                  `containers.${containerIndex}.readinessProbe.httpGet.httpHeadersroot`,
                  "Header values can be at most 128 characters",
                );
              }

              for (const _headerIndexStr in container.readinessProbe.httpGet.httpHeaders) {
                const _headerIndex = Number(_headerIndexStr);
                if (_headerIndex === headerIndex) {
                  continue;
                }

                const _header = container.readinessProbe.httpGet.httpHeaders[_headerIndex] as NameValuePair;
                if (_header.name === header.name) {
                  setError(
                    `containers.${containerIndex}.readinessProbe.httpGet.httpHeaders.${headerIndex}.name`,
                    "Name is not unique",
                  );
                  setError(
                    `containers.${containerIndex}.readinessProbe.httpGet.httpHeadersroot`,
                    "Header names must be unique",
                  );
                }
              }
            }

            if (container.readinessProbe.httpGet.port) {
              xn = Number(container.readinessProbe.httpGet.port);
              if (Number.isNaN(xn)) {
                setError(`containers.${containerIndex}.readinessProbe.httpGet.port`, "Port must be a number");
              } else {
                if (xn < 80) {
                  setError(`containers.${containerIndex}.readinessProbe.httpGet.port`, "Port must be at least 80");
                }
                if (xn > 65535) {
                  setError(`containers.${containerIndex}.readinessProbe.httpGet.port`, "Port can be at most 65535");
                }

                const reserved = [8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000];
                if (reserved.includes(xn)) {
                  setError(`containers.${containerIndex}.readinessProbe.httpGet.port`, "Port number is reserved");
                }
              }
            }
          }

          if (container.readinessProbe.type === "tcp") {
            if (container.readinessProbe.tcpSocket.port) {
              xn = Number(container.readinessProbe.tcpSocket.port);
              if (Number.isNaN(xn)) {
                setError(`containers.${containerIndex}.readinessProbe.tcpSocket.port`, "Port must be a number");
              } else {
                if (xn < 80) {
                  setError(`containers.${containerIndex}.readinessProbe.tcpSocket.port`, "Port must be at least 80");
                }
                if (xn > 65535) {
                  setError(`containers.${containerIndex}.readinessProbe.tcpSocket.port`, "Port can be at most 65535");
                }
                const reserved = [8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000];
                if (reserved.includes(xn)) {
                  setError(`containers.${containerIndex}.readinessProbe.tcpSocket.port`, "Port number is reserved");
                }
              }
            } else {
              setError(`containers.${containerIndex}.readinessProbe.tcpSocket.port`, "Port is required");
            }
          }

          if (container.readinessProbe.type === "grpc") {
            if (container.readinessProbe.grpc.port) {
              xn = Number(container.readinessProbe.grpc.port);
              if (Number.isNaN(xn)) {
                setError(`containers.${containerIndex}.readinessProbe.grpc.port`, "Port must be a number");
              } else {
                if (xn < 80) {
                  setError(`containers.${containerIndex}.readinessProbe.grpc.port`, "Port must be at least 80");
                }
                if (xn > 65535) {
                  setError(`containers.${containerIndex}.readinessProbe.grpc.port`, "Port can be at most 65535");
                }
                const reserved = [8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000];
                if (reserved.includes(xn)) {
                  setError(`containers.${containerIndex}.readinessProbe.grpc.port`, "Port number is reserved");
                }
              }
            } else {
              setError(`containers.${containerIndex}.readinessProbe.grpc.port`, "Port is required");
            }
          }

          if (container.readinessProbe.initialDelaySeconds.length > 0) {
            xn = Number(container.readinessProbe.initialDelaySeconds);
            if (Number.isNaN(xn)) {
              setError(
                `containers.${containerIndex}.readinessProbe.initialDelaySeconds`,
                "Initial Delay must be a number",
              );
            } else {
              if (xn < 0) {
                setError(
                  `containers.${containerIndex}.readinessProbe.initialDelaySeconds`,
                  "Initial Delay must be at least 0",
                );
              }
              if (xn > 600) {
                setError(
                  `containers.${containerIndex}.readinessProbe.initialDelaySeconds`,
                  "Initial Delay can be at most 600",
                );
              }
            }
          }

          if (container.readinessProbe.periodSeconds.length > 0) {
            xn = Number(container.readinessProbe.periodSeconds);
            if (Number.isNaN(xn)) {
              setError(`containers.${containerIndex}.readinessProbe.periodSeconds`, "Period must be a number");
            } else {
              if (xn < 1) {
                setError(`containers.${containerIndex}.readinessProbe.periodSeconds`, "Period must be at least 1");
              }
              if (xn > 600) {
                setError(`containers.${containerIndex}.readinessProbe.periodSeconds`, "Period can be at most 600");
              }
            }
          }

          if (container.readinessProbe.timeoutSeconds.length > 0) {
            xn = Number(container.readinessProbe.timeoutSeconds);
            if (Number.isNaN(xn)) {
              setError(`containers.${containerIndex}.readinessProbe.timeoutSeconds`, "Timeout must be a number");
            } else {
              if (xn < 1) {
                setError(`containers.${containerIndex}.readinessProbe.timeoutSeconds`, "Timeout must be at least 1");
              }
              if (xn > 600) {
                setError(`containers.${containerIndex}.readinessProbe.timeoutSeconds`, "Timeout can be at most 600");
              }
            }
          }

          if (container.readinessProbe.successThreshold.length > 0) {
            xn = Number(container.readinessProbe.successThreshold);
            if (Number.isNaN(xn)) {
              setError(
                `containers.${containerIndex}.readinessProbe.successThreshold`,
                "Success Threshold must be a number",
              );
            } else {
              if (xn < 1) {
                setError(
                  `containers.${containerIndex}.readinessProbe.successThreshold`,
                  "Success Threshold must be at least 1",
                );
              }
              if (xn > 20) {
                setError(
                  `containers.${containerIndex}.readinessProbe.successThreshold`,
                  "Success Threshold can be at most 20",
                );
              }
            }
          }

          if (container.readinessProbe.failureThreshold.length > 0) {
            xn = Number(container.readinessProbe.failureThreshold);
            if (Number.isNaN(xn)) {
              setError(
                `containers.${containerIndex}.readinessProbe.failureThreshold`,
                "Failure Threshold must be a number",
              );
            } else {
              if (xn < 1) {
                setError(
                  `containers.${containerIndex}.readinessProbe.failureThreshold`,
                  "Failure Threshold must be at least 1",
                );
              }
              if (xn > 20) {
                setError(
                  `containers.${containerIndex}.readinessProbe.failureThreshold`,
                  "Failure Threshold can be at most 20",
                );
              }
            }
          }
        }
        // readiness probe end

        // liveness probe start
        if (container.livenessProbe.alreadySet || container.livenessProbe.override) {
          if (container.livenessProbe.type === "exec") {
            if (!container.livenessProbe.exec.command) {
              setError(`containers.${containerIndex}.livenessProbe.exec.command`, "Command is required");
            }

            for (const argIndexStr in container.livenessProbe.exec.args) {
              const argIndex = Number(argIndexStr);
              const arg = container.livenessProbe.exec.args[argIndex];
              if (!arg.value) {
                setError(`containers.${containerIndex}.livenessProbe.exec.args.${argIndex}.value`, "Arg is required");
                setError(
                  `containers.${containerIndex}.livenessProbe.exec.argsroot`,
                  "Some arg fields are empty, either type an arg or remove it",
                );
              }
            }
          }

          if (container.livenessProbe.type === "http") {
            for (const headerIndexStr in container.livenessProbe.httpGet.httpHeaders) {
              const headerIndex = Number(headerIndexStr);
              const header = container.livenessProbe.httpGet.httpHeaders[headerIndex];
              if (!header.name) {
                setError(
                  `containers.${containerIndex}.livenessProbe.httpGet.httpHeaders.${headerIndex}.name`,
                  "Name is required",
                );
                setError(
                  `containers.${containerIndex}.livenessProbe.httpGet.httpHeadersroot`,
                  "Header names are required",
                );
              }
              if (!header.value) {
                setError(
                  `containers.${containerIndex}.livenessProbe.httpGet.httpHeaders.${headerIndex}.value`,
                  "Value is required",
                );
                setError(
                  `containers.${containerIndex}.livenessProbe.httpGet.httpHeadersroot`,
                  "Header values are required",
                );
              }
              if (header.value.length > 128) {
                setError(
                  `containers.${containerIndex}.livenessProbe.httpGet.httpHeaders.${headerIndex}.value`,
                  "Value can be at most 128 characters",
                );
                setError(
                  `containers.${containerIndex}.livenessProbe.httpGet.httpHeadersroot`,
                  "Header values can be at most 128 characters",
                );
              }

              for (const _headerIndexStr in container.livenessProbe.httpGet.httpHeaders) {
                const _headerIndex = Number(_headerIndexStr);
                if (_headerIndex === headerIndex) {
                  continue;
                }

                const _header = container.livenessProbe.httpGet.httpHeaders[_headerIndex] as NameValuePair;
                if (_header.name === header.name) {
                  setError(
                    `containers.${containerIndex}.livenessProbe.httpGet.httpHeaders.${headerIndex}.name`,
                    "Name is not unique",
                  );
                  setError(
                    `containers.${containerIndex}.livenessProbe.httpGet.httpHeadersroot`,
                    "Header names must be unique",
                  );
                }
              }
            }

            if (container.livenessProbe.httpGet.port) {
              xn = Number(container.livenessProbe.httpGet.port);
              if (Number.isNaN(xn)) {
                setError(`containers.${containerIndex}.livenessProbe.httpGet.port`, "Port must be a number");
              } else {
                if (xn < 80) {
                  setError(`containers.${containerIndex}.livenessProbe.httpGet.port`, "Port must be at least 80");
                }
                if (xn > 65535) {
                  setError(`containers.${containerIndex}.livenessProbe.httpGet.port`, "Port can be at most 65535");
                }

                const reserved = [8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000];
                if (reserved.includes(xn)) {
                  setError(`containers.${containerIndex}.livenessProbe.httpGet.port`, "Port number is reserved");
                }
              }
            }
          }

          if (container.livenessProbe.type === "tcp") {
            if (container.livenessProbe.tcpSocket.port) {
              xn = Number(container.livenessProbe.tcpSocket.port);
              if (Number.isNaN(xn)) {
                setError(`containers.${containerIndex}.livenessProbe.tcpSocket.port`, "Port must be a number");
              } else {
                if (xn < 80) {
                  setError(`containers.${containerIndex}.livenessProbe.tcpSocket.port`, "Port must be at least 80");
                }
                if (xn > 65535) {
                  setError(`containers.${containerIndex}.livenessProbe.tcpSocket.port`, "Port can be at most 65535");
                }
                const reserved = [8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000];
                if (reserved.includes(xn)) {
                  setError(`containers.${containerIndex}.livenessProbe.tcpSocket.port`, "Port number is reserved");
                }
              }
            } else {
              setError(`containers.${containerIndex}.livenessProbe.tcpSocket.port`, "Port is required");
            }
          }

          if (container.livenessProbe.type === "grpc") {
            if (container.livenessProbe.grpc.port) {
              xn = Number(container.livenessProbe.grpc.port);
              if (Number.isNaN(xn)) {
                setError(`containers.${containerIndex}.livenessProbe.grpc.port`, "Port must be a number");
              } else {
                if (xn < 80) {
                  setError(`containers.${containerIndex}.livenessProbe.grpc.port`, "Port must be at least 80");
                }
                if (xn > 65535) {
                  setError(`containers.${containerIndex}.livenessProbe.grpc.port`, "Port can be at most 65535");
                }
                const reserved = [8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000];
                if (reserved.includes(xn)) {
                  setError(`containers.${containerIndex}.livenessProbe.grpc.port`, "Port number is reserved");
                }
              }
            } else {
              setError(`containers.${containerIndex}.livenessProbe.grpc.port`, "Port is required");
            }
          }

          if (container.livenessProbe.initialDelaySeconds.length > 0) {
            xn = Number(container.livenessProbe.initialDelaySeconds);
            if (Number.isNaN(xn)) {
              setError(
                `containers.${containerIndex}.livenessProbe.initialDelaySeconds`,
                "Initial Delay must be a number",
              );
            } else {
              if (xn < 0) {
                setError(
                  `containers.${containerIndex}.livenessProbe.initialDelaySeconds`,
                  "Initial Delay must be at least 0",
                );
              }
              if (xn > 600) {
                setError(
                  `containers.${containerIndex}.livenessProbe.initialDelaySeconds`,
                  "Initial Delay can be at most 600",
                );
              }
            }
          }

          if (container.livenessProbe.periodSeconds.length > 0) {
            xn = Number(container.livenessProbe.periodSeconds);
            if (Number.isNaN(xn)) {
              setError(`containers.${containerIndex}.livenessProbe.periodSeconds`, "Period must be a number");
            } else {
              if (xn < 1) {
                setError(`containers.${containerIndex}.livenessProbe.periodSeconds`, "Period must be at least 1");
              }
              if (xn > 600) {
                setError(`containers.${containerIndex}.livenessProbe.periodSeconds`, "Period can be at most 600");
              }
            }
          }

          if (container.livenessProbe.timeoutSeconds.length > 0) {
            xn = Number(container.livenessProbe.timeoutSeconds);
            if (Number.isNaN(xn)) {
              setError(`containers.${containerIndex}.livenessProbe.timeoutSeconds`, "Timeout must be a number");
            } else {
              if (xn < 1) {
                setError(`containers.${containerIndex}.livenessProbe.timeoutSeconds`, "Timeout must be at least 1");
              }
              if (xn > 600) {
                setError(`containers.${containerIndex}.livenessProbe.timeoutSeconds`, "Timeout can be at most 600");
              }
            }
          }

          if (container.livenessProbe.successThreshold.length > 0) {
            xn = Number(container.livenessProbe.successThreshold);
            if (Number.isNaN(xn)) {
              setError(
                `containers.${containerIndex}.livenessProbe.successThreshold`,
                "Success Threshold must be a number",
              );
            } else {
              if (xn < 1) {
                setError(
                  `containers.${containerIndex}.livenessProbe.successThreshold`,
                  "Success Threshold must be at least 1",
                );
              }
              if (xn > 20) {
                setError(
                  `containers.${containerIndex}.livenessProbe.successThreshold`,
                  "Success Threshold can be at most 20",
                );
              }
            }
          }

          if (container.livenessProbe.failureThreshold.length > 0) {
            xn = Number(container.livenessProbe.failureThreshold);
            if (Number.isNaN(xn)) {
              setError(
                `containers.${containerIndex}.livenessProbe.failureThreshold`,
                "Failure Threshold must be a number",
              );
            } else {
              if (xn < 1) {
                setError(
                  `containers.${containerIndex}.livenessProbe.failureThreshold`,
                  "Failure Threshold must be at least 1",
                );
              }
              if (xn > 20) {
                setError(
                  `containers.${containerIndex}.livenessProbe.failureThreshold`,
                  "Failure Threshold can be at most 20",
                );
              }
            }
          }
        }
        // liveness probe end

        // metrics start
        if (container.metrics.isEnabled) {
          if (!container.metrics.port) {
            setError(`containers.${containerIndex}.metrics.port`, "Port is required");
          } else {
            xn = Number(container.metrics.port);
            if (Number.isNaN(xn)) {
              setError(`containers.${containerIndex}.metrics.port`, "Port must be a number");
            } else {
              if (xn < 80) {
                setError(`containers.${containerIndex}.metrics.port`, "Port must be at least 80");
              }
              if (xn > 65535) {
                setError(`containers.${containerIndex}.metrics.port`, "Port can be at most 65535");
              }
              const reserved = [8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000];
              if (reserved.includes(xn)) {
                setError(`containers.${containerIndex}.metrics.port`, "Port number is reserved");
              }
            }
          }

          for (const dropMetricsRegexIndexStr in container.metrics.dropMetrics) {
            const dropMetricsRegexIndex = Number(dropMetricsRegexIndexStr);
            const dropMetricsRegex = container.metrics.dropMetrics[dropMetricsRegexIndex];
            if (!dropMetricsRegex.value) {
              setError(
                `containers.${containerIndex}.metrics.dropMetrics.${dropMetricsRegexIndex}.value`,
                "Regex is required",
              );
              setError(
                `containers.${containerIndex}.metrics.dropMetricsroot`,
                "Either type a regex to drop metrics item or remove it",
              );
            }
          }
        }
        // metrics end

        // resources start
        const hasRelaxedRatioTag = v.tags?.some((t: any) => t.name === "cpln/relaxMemoryToCpuRatio") || false;
        const resourceValidations = validateContainerResources({
          type: v.type,
          capacityAI: v.defaultOptions.capacityAI,
          hasRelaxedRatioTag,
          minCpuAmount: container.minCpu.amount,
          minCpuUnit: container.minCpu.unit,
          maxCpuAmount: container.cpu.amount,
          maxCpuUnit: container.cpu.unit,
          minMemoryAmount: container.minMemory.amount,
          minMemoryUnit: container.minMemory.unit,
          maxMemoryAmount: container.memory.amount,
          maxMemoryUnit: container.memory.unit,
          gpuType: container.gpu?.type,
          gpuNvidiaModel: container.gpu?.nvidia?.model,
          gpuNvidiaQuantity: container.gpu?.nvidia?.quantity,
        });

        for (const validation of resourceValidations) {
          const fieldPath = getFieldPathForValidation(containerIndex, validation);
          setError(fieldPath, validation.shortMessage);
        }
        // resources end
      }
      // containers end

      // securityOptions start
      if (v.securityOptions.filesystemGroupId.length > 0) {
        xn = Number(v.securityOptions.filesystemGroupId);
        if (Number.isNaN(xn)) {
          setError(`securityOptions.filesystemGroupId`, "File System Group Id must be a number");
        } else {
          if (xn < 1) {
            setError(`securityOptions.filesystemGroupId`, `File System Group Id must be at least 1`);
          }
          if (xn > 65534) {
            setError(`securityOptions.filesystemGroupId`, "File System Group Id can be at most 65534");
          }
        }
      }
      if (v.securityOptions.runAsUser.length > 0) {
        xn = Number(v.securityOptions.runAsUser);
        if (Number.isNaN(xn)) {
          setError(`securityOptions.runAsUser`, "Run as User must be a number");
        } else {
          if (xn < 1) {
            setError(`securityOptions.runAsUser`, `Run as User must be at least 1`);
          }
          if (xn > 65534) {
            setError(`securityOptions.runAsUser`, "Run as User can be at most 65534");
          }
        }
      }
      // securityOptions end

      // rolloutOptions start
      if (v.rolloutOptions.terminationGracePeriodSeconds.length > 0) {
        xn = Number(v.rolloutOptions.terminationGracePeriodSeconds);
        if (Number.isNaN(xn)) {
          setError(`rolloutOptions.terminationGracePeriodSeconds`, "Termination Grace Period must be a number");
        } else {
          if (xn < 0) {
            setError(`rolloutOptions.terminationGracePeriodSeconds`, `Termination Grace Period must be at least 0`);
          }
          if (xn > 900) {
            setError(`rolloutOptions.terminationGracePeriodSeconds`, "Termination Grace Period can be at most 900");
          }
        }
      }
      // rolloutOptions end

      // requestRetryPolicy start
      if (v.requestRetryPolicy.attempts.length > 0) {
        xn = Number(v.requestRetryPolicy.attempts);
        if (Number.isNaN(xn)) {
          setError(`requestRetryPolicy.attempts`, "Attempt must be a number");
        } else {
          if (xn < 0) {
            setError(`requestRetryPolicy.attempts`, `Attempt must be at least 0`);
          }
          if (xn > Math.pow(2, 32) - 1) {
            setError(`requestRetryPolicy.attempts`, "Attempt can be at most 2³² - 1");
          }
        }
      }
      for (const indexStr in v.requestRetryPolicy.retryOn) {
        const index = Number(indexStr);
        const value = v.requestRetryPolicy.retryOn[index];

        if (value.length > 32) {
          setError(`requestRetryPolicy.retryOn`, "Each value can be at most 32 characters");
        }
      }
      // requestRetryPolicy end

      // firewall start
      if (!v.firewallConfig.external.inbound.public) {
        for (const indexStr in v.firewallConfig.external.inbound.allowCidr) {
          const index = Number(indexStr);
          const cidr = v.firewallConfig.external.inbound.allowCidr[index];

          if (!cidr.value) {
            setError(`firewallConfig.external.inbound.allowCidr.${index}.value`, "CIDR is required");
            setError(`firewallConfig.external.inbound.allowCidrroot`, "Either type the CIDR or remove the item");
          } else {
            for (const _indexStr in v.firewallConfig.external.inbound.allowCidr) {
              const _index = Number(_indexStr);
              if (index === _index) {
                continue;
              }

              const _cidr = v.firewallConfig.external.inbound.allowCidr[_index];

              if (_cidr.value === cidr.value) {
                setError(`firewallConfig.external.inbound.allowCidr.${index}.value`, "CIDR is not unique");
                setError(`firewallConfig.external.inbound.allowCidrroot`, "CIDR values must be unique");
              }
            }
          }
        }
      }
      for (const indexStr in v.firewallConfig.external.inbound.blockedCidr) {
        const index = Number(indexStr);
        const cidr = v.firewallConfig.external.inbound.blockedCidr[index];

        if (!cidr.value) {
          setError(`firewallConfig.external.inbound.blockedCidr.${index}.value`, "CIDR is required");
          setError(`firewallConfig.external.inbound.blockedCidrroot`, "Either type the CIDR or remove the item");
        } else {
          for (const _indexStr in v.firewallConfig.external.inbound.blockedCidr) {
            const _index = Number(_indexStr);
            if (index === _index) {
              continue;
            }

            const _cidr = v.firewallConfig.external.inbound.blockedCidr[_index];

            if (_cidr.value === cidr.value) {
              setError(`firewallConfig.external.inbound.blockedCidr.${index}.value`, "CIDR is not unique");
              setError(`firewallConfig.external.inbound.blockedCidrroot`, "CIDR values must be unique");
            }
          }
        }
      }
      for (const indexStr in v.firewallConfig.external.httpInboundHeaderFilter) {
        const index = Number(indexStr);
        const filter = v.firewallConfig.external.httpInboundHeaderFilter[index];

        if (!filter.key) {
          setError(`firewallConfig.external.httpInboundHeaderFilter.${index}.key`, "Key is required");
          setError(
            `firewallConfig.external.httpInboundHeaderFilterroot`,
            "Either type the Filter key or remove the item",
          );
        } else {
          for (const _indexStr in v.firewallConfig.external.httpInboundHeaderFilter) {
            const _index = Number(_indexStr);
            if (index === _index) {
              continue;
            }

            const _filter = v.firewallConfig.external.httpInboundHeaderFilter[_index];

            if (_filter.key === filter.key) {
              setError(`firewallConfig.external.httpInboundHeaderFilter.${index}.key`, "Key is not unique");
              setError(`firewallConfig.external.httpInboundHeaderFilterroot`, "Filter keys must be unique");
            }
          }
        }
      }

      if (!v.firewallConfig.external.outbound.public) {
        for (const indexStr in v.firewallConfig.external.outbound.allowCidr) {
          const index = Number(indexStr);
          const cidr = v.firewallConfig.external.outbound.allowCidr[index];

          if (!cidr.value) {
            setError(`firewallConfig.external.outbound.allowCidr.${index}.value`, "CIDR is required");
            setError(`firewallConfig.external.outbound.allowCidrroot`, "Either type the CIDR or remove the item");
          } else {
            for (const _indexStr in v.firewallConfig.external.outbound.allowCidr) {
              const _index = Number(_indexStr);
              if (index === _index) {
                continue;
              }

              const _cidr = v.firewallConfig.external.outbound.allowCidr[_index];

              if (_cidr.value === cidr.value) {
                setError(`firewallConfig.external.outbound.allowCidr.${index}.value`, "CIDR is not unique");
                setError(`firewallConfig.external.outbound.allowCidrroot`, "CIDR values must be unique");
              }
            }
          }
        }
        for (const indexStr in v.firewallConfig.external.outbound.allowHostname) {
          const index = Number(indexStr);
          const hostname = v.firewallConfig.external.outbound.allowHostname[index];

          if (!hostname.value) {
            setError(`firewallConfig.external.outbound.allowHostname.${index}.value`, "Hostname is required");
            setError(
              `firewallConfig.external.outbound.allowHostnameroot`,
              "Either type the hostname or remove the item",
            );
          } else {
            for (const _indexStr in v.firewallConfig.external.outbound.allowHostname) {
              const _index = Number(_indexStr);
              if (index === _index) {
                continue;
              }

              const _hostname = v.firewallConfig.external.outbound.allowHostname[_index];

              if (_hostname.value === hostname.value) {
                setError(`firewallConfig.external.outbound.allowHostname.${index}.value`, "Hostname is not unique");
                setError(`firewallConfig.external.outbound.allowHostnameroot`, "Hostname values must be unique");
              }
            }
          }
        }
      }
      for (const indexStr in v.firewallConfig.external.outbound.blockedCidr) {
        const index = Number(indexStr);
        const cidr = v.firewallConfig.external.outbound.blockedCidr[index];

        if (!cidr.value) {
          setError(`firewallConfig.external.outbound.blockedCidr.${index}.value`, "CIDR is required");
          setError(`firewallConfig.external.outbound.blockedCidrroot`, "Either type the CIDR or remove the item");
        } else {
          for (const _indexStr in v.firewallConfig.external.outbound.blockedCidr) {
            const _index = Number(_indexStr);
            if (index === _index) {
              continue;
            }

            const _cidr = v.firewallConfig.external.outbound.blockedCidr[_index];

            if (_cidr.value === cidr.value) {
              setError(`firewallConfig.external.outbound.blockedCidr.${index}.value`, "CIDR is not unique");
              setError(`firewallConfig.external.outbound.blockedCidrroot`, "CIDR values must be unique");
            }
          }
        }
      }
      for (const indexStr in v.firewallConfig.external.outbound.allowPort) {
        const index = Number(indexStr);
        const port = v.firewallConfig.external.outbound.allowPort[index];

        if (!port.number) {
          setError(`firewallConfig.external.outbound.allowPort.${index}.number`, "Port number is required");
          setError(`firewallConfig.external.outbound.allowPortroot`, "Port numbers are required");
        } else {
          for (const _indexStr in v.firewallConfig.external.outbound.allowPort) {
            const _index = Number(_indexStr);
            if (index === _index) {
              continue;
            }

            const _port = v.firewallConfig.external.outbound.allowPort[_index];

            if (_port.protocol === port.protocol && _port.number === port.number) {
              setError(`firewallConfig.external.outbound.allowPort.${index}.protocol`, "Port is not unique");
              setError(`firewallConfig.external.outbound.allowPort.${index}.number`, "Port is not unique");
              setError(`firewallConfig.external.outbound.allowPortroot`, "Port values must be unique");
            }
          }
        }
      }
      // firewall end

      // load balancer start
      if (v.loadBalancer.direct.isEnabled) {
        for (const indexStr in v.loadBalancer.direct.ports) {
          const index = Number(indexStr);
          const port = v.loadBalancer.direct.ports[index];

          if (port.externalPort.length < 1) {
            setError(`loadBalancer.direct.ports.${index}.externalPort`, "External port is required");
            setError(`loadBalancer.direct.portsroot`, "External ports are required");
          } else {
            xn = Number(port.externalPort);
            if (Number.isNaN(xn)) {
              setError(`loadBalancer.direct.ports.${index}.externalPort`, "External Port must be a number");
              setError(`loadBalancer.direct.portsroot`, `External Ports must be a number`);
            } else {
              if (xn < 22) {
                setError(`loadBalancer.direct.ports.${index}.externalPort`, `External Port must be at least 22`);
                setError(`loadBalancer.direct.portsroot`, `External Ports must be at least 22`);
              }
              if (xn > 32768) {
                setError(`loadBalancer.direct.ports.${index}.externalPort`, "External Port can be at most 32768");
                setError(`loadBalancer.direct.portsroot`, `External Ports must be at most 32768`);
              }
            }
          }

          if (port.containerPort.length > 0) {
            xn = Number(port.containerPort);
            if (Number.isNaN(xn)) {
              setError(`loadBalancer.direct.ports.${index}.containerPort`, "Container Port must be a number");
            } else {
              if (xn < 80) {
                setError(`loadBalancer.direct.ports.${index}.containerPort`, "Container Port must be at least 80");
                setError(`loadBalancer.direct.portsroot`, `Container Ports must be at least 80`);
              }
              if (xn > 65535) {
                setError(`loadBalancer.direct.ports.${index}.containerPort`, "Container Port can be at most 65535");
                setError(`loadBalancer.direct.portsroot`, `Container Ports can be at most 65535`);
              }
            }

            const reserved = [8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000];
            if (reserved.includes(xn)) {
              setError(`loadBalancer.direct.ports.${index}.containerPort`, "Container Port number is reserved");
              setError(
                `loadBalancer.direct.portsroot`,
                `Container ports cannot be reserved values ([8012, 8022, 9090, 9091, 15000, 15001, 15006, 15020, 15021, 15090, 41000])`,
              );
            }
          }

          if (!port.protocol) {
            setError(`loadBalancer.direct.ports.${index}.protocol`, "Protocol is required");
            setError(`loadBalancer.direct.portsroot`, "Protocols are required");
          }
        }
      }

      if (v.loadBalancer.geoLocation.isEnabled) {
        if (v.loadBalancer.geoLocation.headers.asn.length > 128) {
          setError(`loadBalancer.geoLocation.headers.asn`, "ASN value can be at most 128 characters");
        }
        if (v.loadBalancer.geoLocation.headers.city.length > 128) {
          setError(`loadBalancer.geoLocation.headers.city`, "City value can be at most 128 characters");
        }
        if (v.loadBalancer.geoLocation.headers.country.length > 128) {
          setError(`loadBalancer.geoLocation.headers.country`, "Country value can be at most 128 characters");
        }
        if (v.loadBalancer.geoLocation.headers.region.length > 128) {
          setError(`loadBalancer.geoLocation.headers.region`, "Region value can be at most 128 characters");
        }
      }
      // load balancer end

      if (v.extras) {
        try {
          jsYaml.load(v.extras);
        } catch (e) {
          setError(`extras`, "Extras must be valid YAML");
        }
      }

      // sidecar start
      if (v.type !== "cron") {
        if (v.sidecar.envoy.type === "jwt") {
          if (!v.sidecar.envoy.jwt.name) {
            setError(`sidecar.envoy.jwt.name`, "Name is required");
          }
          if (!v.sidecar.envoy.jwt.issuer) {
            setError(`sidecar.envoy.jwt.issuer`, "Issuer is required");
          }
          if (!v.sidecar.envoy.jwt.remote_jwks_http_uri) {
            setError(`sidecar.envoy.jwt.remote_jwks_http_uri`, "URI is required");
          }
          if (!v.sidecar.envoy.jwt.remote_jwks_http_uri_timeout) {
            setError(`sidecar.envoy.jwt.remote_jwks_http_uri_timeout`, "Timeout is required");
          }

          for (const audienceIndexStr in v.sidecar.envoy.jwt.audiences) {
            const audienceIndex = Number(audienceIndexStr);
            const audience = v.sidecar.envoy.jwt.audiences[audienceIndex];
            if (!audience) {
              setError(`sidecar.envoy.jwt.audiences.${audienceIndex}`, "Audience is required");
              setError(`sidecar.envoy.jwt.audiencesroot`, "Audience items cannot be left empty");
            }
          }

          for (const headerIndexStr in v.sidecar.envoy.jwt.claim_to_headers) {
            const headerIndex = Number(headerIndexStr);
            const header = v.sidecar.envoy.jwt.claim_to_headers[headerIndex];
            if (!header.name) {
              setError(`sidecar.envoy.jwt.claim_to_headers.${headerIndex}.name`, "Header name is required");
              setError(`sidecar.envoy.jwt.claim_to_headersroot`, "Header names are required");
            }
            if (!header.value) {
              setError(`sidecar.envoy.jwt.claim_to_headers.${headerIndex}.value`, "Header value is required");
              setError(`sidecar.envoy.jwt.claim_to_headersroot`, "Header values are required");
            }
          }

          for (const ruleIndexStr in v.sidecar.envoy.jwt.rules) {
            const ruleIndex = Number(ruleIndexStr);
            const rule = v.sidecar.envoy.jwt.rules[ruleIndex];
            if (!rule.prefix) {
              setError(`sidecar.envoy.jwt.rules.${ruleIndex}.prefix`, "Path is required");
              setError(`sidecar.envoy.jwt.rulesroot`, "Path for each item is required");
            }
            for (const ruleHeaderIndexStr in rule.headers) {
              const ruleHeaderIndex = Number(ruleHeaderIndexStr);
              const header = rule.headers[ruleHeaderIndex];
              if (!header.name) {
                setError(
                  `sidecar.envoy.jwt.rules.${ruleIndex}.headers.${ruleHeaderIndex}.name`,
                  "Header name is required",
                );
                setError(`sidecar.envoy.jwt.rules.${ruleIndex}.headersroot`, "Header names are required");
              }
              if (!header.value) {
                setError(
                  `sidecar.envoy.jwt.rules.${ruleIndex}.headers.${ruleHeaderIndex}.value`,
                  "Header value is required",
                );
                setError(`sidecar.envoy.jwt.rules.${ruleIndex}.headersroot`, "Header values are required");
              }
            }
          }
        }
      }

      if (v.sidecar.envoy.type === "custom") {
        if (!v.sidecar.envoy.custom) {
          setError(`sidecar.envoy.custom`, "Custom envoy configuration is required");
        } else {
          try {
            jsYaml.load(v.sidecar.envoy.custom);
          } catch (e: any) {
            setError(`sidecar.envoy.custom`, `Invalid YAML: ${e.message}`);
          }
        }
      }
      // sidecar end

      // tags start
      for (const tagIndexStr in v.tags) {
        const tagIndex = Number(tagIndexStr);
        const tag = v.tags[tagIndex];
        if (!tag.name) {
          setError(`tags.${tagIndex}.name`, "Tag key is required");
          setError(`tagsroot`, "Tag keys are required");
        }
      }
      // tags end

      // dryrun requires gvc
      // if (v.gvc?.value) {
      //   const org = store.getState().context.org;
      // TODO get gvc name for below from gvc.link
      //   const ctx = { org, gvc: v.gvcName };
      //   const body = inputToWorkload(v);
      //   try {
      //     const res = await request({ method: "post", url: parentLink("workload", ctx) + `?dryRun`, body: body });
      //     console.log(res);
      //   } catch (e) {
      //     let errorMessage = e?.response?.data?.message;
      //     if (!errorMessage) errorMessage = e.message;
      //     console.log("dryRunError", errorMessage);
      //   }
      // }

      // setup
      for (const path of Object.keys(e)) {
        const value = e[path];
        formSet(id, `errors.${path}`, value);
      }
      for (const path of Object.keys(w)) {
        const value = w[path];
        formSet(id, `warnings.${path}`, value);
      }

      const hasErrors = Object.keys(e).length > 0;

      formSet(id, `meta.valid`, !hasErrors);
      formSet(id, `meta.validating`, false);

      return hasErrors;
    } catch (e) {
      formSet(id, `errors`, { root: e.message });
      return true;
    }
  };
}
